import React from 'react';
import { Button, Tooltip } from '@mui/material';
import { CompareArrows } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { MAX_COMPARE_PRODUCTS, MIN_COMPARE_PRODUCTS } from '../../types/productComparison';

interface CompareSelectionButtonProps {
  /** The `product_comparison` flag for a store session. Off renders nothing at all. */
  enabled: boolean;
  /** Products ticked with the browser's one selection checkbox. */
  selectedCount: number;
  /** "Select all N filtered" mode: the ids are not all known, so nothing can be compared. */
  allFilteredSelected: boolean;
  onOpen: () => void;
}

/** True when the current selection can be opened in the comparison view. */
export const canCompareSelection = (selectedCount: number, allFilteredSelected: boolean): boolean =>
  !allFilteredSelected && selectedCount >= MIN_COMPARE_PRODUCTS && selectedCount <= MAX_COMPARE_PRODUCTS;

/** The comparison page URL for the ticked products, in tick order. */
export const buildCompareUrl = (productIds: Iterable<string>): string =>
  `/app/compare?ids=${encodeURIComponent(Array.from(productIds).join(','))}`;

/**
 * Compare button in the product browser's bulk-action toolbar, beside Add to sync and
 * Remove from sync. Enabled for 2 to 4 selected products; otherwise disabled with a hint.
 */
export const CompareSelectionButton: React.FC<CompareSelectionButtonProps> = ({
  enabled,
  selectedCount,
  allFilteredSelected,
  onOpen,
}) => {
  const { t } = useTranslation() as { t: (key: string, options?: Record<string, unknown>) => string };
  if (!enabled) return null;
  const ready = canCompareSelection(selectedCount, allFilteredSelected);

  return (
    // A disabled button fires no pointer events, so the span carries the hint.
    <Tooltip title={ready ? '' : t('productCompare.toolbar.hint')} arrow>
      <span>
        <Button
          size="small"
          variant="outlined"
          startIcon={<CompareArrows sx={{ fontSize: 15 }} />}
          onClick={onOpen}
          disabled={!ready}
          sx={{ height: 30, whiteSpace: 'nowrap' }}
        >
          {t('productCompare.toolbar.compare')}
        </Button>
      </span>
    </Tooltip>
  );
};
